import { useNavigate, useParams } from 'react-router-dom';
import { useFetch } from '../../hooks/useFetch';
import LoadingContent from '../../components/LoadingContent';
import { useAuth } from '../../context/AuthContext';
import {
    Alert,
    Avatar,
    Box,
    Button,
    Chip,
    Stack,
    Typography,
} from '@mui/material';
import {
    addMinutes,
    differenceInSeconds,
    isPast,
    parseISO,
} from 'date-fns';
import { useEffect, useState } from 'react';
import useHandleErrors from '../../hooks/useHandleErrors';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import EventBusyIcon from '@mui/icons-material/EventBusy';
import MicIcon from '@mui/icons-material/Mic';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import VideocamIcon from '@mui/icons-material/Videocam';
import BotaoConversar from '../../components/BotaoConversar';
import PaymentInfo from './components/PaymentInfo';
import HorarioAlterado from './components/HorarioAlterado';
import CancelarVaga from './components/CancelarVaga';
import { getServerNow, useServerTime } from '../../hooks/useServerTime';

const SectionCard = ({ children }) => (
    <Box sx={{
        width: '100%',
        bgcolor: 'white',
        borderRadius: '16px',
        border: '1px solid #e8e8e8',
        p: { xs: '20px 16px', sm: '28px 24px' },
    }}>
        {children}
    </Box>
);

const SectionTitle = ({ children }) => (
    <Typography variant="subtitle1" fontWeight={600} color="#333" sx={{ mb: 1.5 }}>
        {children}
    </Typography>
);

function calculateTimeRemaining(datetime) {
    const currentDate = getServerNow();
    const timeDifference = differenceInSeconds(datetime, currentDate);
    const fifteenMinutesAfter = differenceInSeconds(currentDate, datetime) > 3600;

    if (timeDifference <= -2) {
        return { days: 0, hours: 0, minutes: 0, seconds: 0, total: 0, expired: fifteenMinutesAfter };
    }

    const days = Math.floor(timeDifference / (60 * 60 * 24));
    const hours = Math.floor((timeDifference % (60 * 60 * 24)) / (60 * 60));
    const minutes = Math.floor((timeDifference % (60 * 60)) / 60);
    const seconds = timeDifference % 60;

    return { days, hours, minutes, seconds, total: timeDifference, expired: false };
}

const CountdownButton = ({ datetime, visible, chosen, raffle, id, scheduleId, firstJoinedAt, finished, isOwner }) => {
    useServerTime(); // Garante sincronização com servidor antes de calcular countdown
    const [timeRemaining, setTimeRemaining] = useState(calculateTimeRemaining(parseISO(datetime)));
    const [disabled, setDisabled] = useState(true);
    const navigation = useNavigate();
    const [expired, setExpired] = useState(false);
    // Encontro finalizado nao reabre nem para quem ja tinha entrado
    const canReconnect = Boolean(firstJoinedAt) && !finished;

    useEffect(() => {
        const intervalId = setInterval(() => {
            const newTime = calculateTimeRemaining(parseISO(datetime));
            setTimeRemaining(newTime);
            if (newTime.total <= 0) {
                if (newTime.expired && !canReconnect) {
                    setDisabled(true);
                    setExpired(true);
                    clearInterval(intervalId);
                } else {
                    setDisabled(false);
                }
            }
        }, 1000);
        return () => clearInterval(intervalId);
    }, [datetime, canReconnect]);

    // Depois dos hooks, para nao mudar a quantidade de hooks entre renders
    if (finished) {
        return (
            <Alert severity="info" sx={{ width: '100%', borderRadius: '12px' }}>
                Este encontro foi finalizado.
            </Alert>
        );
    }

    const formatTime = () => {
        const { days, hours, minutes, seconds } = timeRemaining;
        const parts = [];
        if (days > 0) parts.push(`${days}d`);
        if (hours > 0) parts.push(`${hours}h`);
        parts.push(`${minutes}min`);
        if (days === 0) parts.push(`${seconds}s`);
        return parts.join(' ');
    };

    const getAction = () => {
        // O anfitriao conduz o encontro: em sorteio ele nao e "sorteado", entao
        // a regra do chosen o mandava para a transmissao em vez da sala
        if (isOwner) return { label: 'Entrar no encontro', path: `/encontros/${id}/chamada` };
        if (raffle) {
            if (visible && !chosen) return { label: 'Assistir como espectador', path: `/lista/ao-vivo/${scheduleId}` };
            if (chosen || (!visible && chosen)) return { label: 'Entrar no encontro', path: `/encontros/${id}/chamada` };
            if (!visible && !chosen) return { label: 'O encontro é privado', path: null };
        }
        return { label: 'Entrar no encontro', path: `/encontros/${id}/chamada` };
    };

    const action = getAction();

    return (
        <Stack spacing={1.5} sx={{ width: '100%' }}>
            {expired && !canReconnect && (
                <Alert severity="warning" sx={{ borderRadius: '12px' }}>
                    O tempo do encontro foi encerrado.
                </Alert>
            )}
            {canReconnect && timeRemaining.total <= 0 && timeRemaining.expired && (
                <Alert severity="info" sx={{ borderRadius: '12px' }}>
                    Você pode reconectar ao encontro pois já havia entrado anteriormente.
                </Alert>
            )}
            {!expired && timeRemaining.total > 0 && (
                <SectionCard>
                    <Box sx={{ textAlign: 'center' }}>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                            O encontro começa em
                        </Typography>
                        <Typography variant="h5" fontWeight={700} color="#006a61">
                            {formatTime()}
                        </Typography>
                    </Box>
                </SectionCard>
            )}
            <Button
                variant="contained"
                fullWidth
                disabled={disabled || !action.path}
                onClick={() => action.path && navigation(action.path)}
                startIcon={<VideocamIcon />}
                sx={{
                    bgcolor: '#006a61',
                    borderRadius: '8px',
                    height: 48,
                    textTransform: 'none',
                    fontWeight: 600,
                    fontSize: '1rem',
                    '&:hover': { bgcolor: '#005a52' },
                }}
            >
                {timeRemaining.total <= 0 ? action.label : 'Aguardando horário'}
            </Button>
        </Stack>
    );
};

export default () => {
    const params = useParams();
    const { user } = useAuth();
    const [errs, setErrs] = useState();
    useHandleErrors(errs);
    const navigation = useNavigate();
    const { data, error, mutate, isLoading } = useFetch(`/booking/${params.id}`);
    useHandleErrors(error);

    const BackButton = () => (
        <Box sx={{ width: '100%', maxWidth: 720, mx: 'auto', px: { xs: '12px', sm: '16px' }, pt: 2 }}>
            <Button
                startIcon={<ArrowBackIcon />}
                onClick={() => navigation('/calendario')}
                sx={{ textTransform: 'none', color: '#666' }}
            >
                Calendário
            </Button>
        </Box>
    );

    if (isLoading || !data) return <><BackButton /><LoadingContent /></>;

    const { meeting, meeting_scheduled, chosen, datetime, id, isHost } = data;
    const isOwner = isHost || meeting?.Profile?.User?.id === user?.id;

    if (!meeting) {
        return (
            <>
                <BackButton />
                <Box sx={{ maxWidth: 720, mx: 'auto', p: 3, textAlign: 'center' }}>
                    <Alert severity="error" sx={{ borderRadius: '12px' }}>
                        Não foi possível carregar os dados do encontro.
                    </Alert>
                </Box>
            </>
        );
    }

    const hostUser = meeting?.Profile?.User;
    const meetingDate = new Date(datetime);
    const formattedDate = meetingDate.toLocaleDateString('pt-BR', { weekday: 'long', day: '2-digit', month: 'long' });
    const formattedTime = meetingDate.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });

    return (
        <Box sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            width: '100%',
            maxWidth: 720,
            mx: 'auto',
            p: { xs: '16px 12px 32px', sm: '24px 16px 48px' },
            gap: 3,
        }}>
            {/* Header com voltar */}
            <Box sx={{ width: '100%', display: 'flex', alignItems: 'center', gap: 1 }}>
                <Button
                    startIcon={<ArrowBackIcon />}
                    onClick={() => navigation('/calendario')}
                    sx={{ textTransform: 'none', color: '#666' }}
                >
                    Calendário
                </Button>
            </Box>

            {/* Info do encontro */}
            <SectionCard>
                <Stack direction="row" spacing={2} alignItems="center">
                    <Avatar
                        src={hostUser?.profileImage}
                        alt={hostUser?.name}
                        sx={{
                            width: 56,
                            height: 56,
                            border: '2px solid #e0e0e0',
                            cursor: 'pointer',
                        }}
                        onClick={() => navigation(`/anfitriao/@${hostUser?.username}`)}
                    >
                        {hostUser?.name?.[0]}
                    </Avatar>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="subtitle1" fontWeight={600} sx={{ lineHeight: 1.3 }}>
                            {meeting.title}
                        </Typography>
                        <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
                            onClick={() => navigation(`/anfitriao/@${hostUser?.username}`)}
                        >
                            Anfitrião: @{hostUser?.username}
                        </Typography>
                    </Box>
                </Stack>

                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 2 }}>
                    <Chip
                        size="small"
                        label={meeting.raffle ? 'Seleção' : 'Agendamento'}
                        sx={{ bgcolor: '#e6f3f1', color: '#006a61', fontWeight: 500 }}
                    />
                    <Chip
                        size="small"
                        label={meeting.visible_public ? 'Publico' : 'Privado'}
                        sx={{ bgcolor: '#e6f3f1', color: '#006a61', fontWeight: 500 }}
                    />
                    {meeting.free
                        ? <Chip size="small" label="Gratuito" sx={{ bgcolor: '#e8f5e9', color: '#2e7d32', fontWeight: 500 }} />
                        : <Chip size="small" label={`R$ ${Number(meeting.pricing).toFixed(2).replace('.', ',')}`} sx={{ bgcolor: '#fef3c7', color: '#b45309', fontWeight: 600 }} />
                    }
                </Stack>
            </SectionCard>

            {/* Data e horario */}
            <SectionCard>
                <Stack spacing={1.5}>
                    <Stack direction="row" spacing={1.5} alignItems="center">
                        <CalendarTodayIcon sx={{ fontSize: 20, color: '#006a61' }} />
                        <Box>
                            <Typography variant="body2" color="text.secondary">Data</Typography>
                            <Typography variant="subtitle2" fontWeight={600} sx={{ textTransform: 'capitalize' }}>
                                {formattedDate}
                            </Typography>
                        </Box>
                    </Stack>
                    <Stack direction="row" spacing={1.5} alignItems="center">
                        <AccessTimeIcon sx={{ fontSize: 20, color: '#006a61' }} />
                        <Box>
                            <Typography variant="body2" color="text.secondary">Horário</Typography>
                            <Typography variant="subtitle2" fontWeight={600}>
                                {formattedTime}
                            </Typography>
                        </Box>
                    </Stack>
                    <Stack direction="row" spacing={1.5} alignItems="center">
                        {isOwner
                            ? <MicIcon sx={{ fontSize: 20, color: '#006a61' }} />
                            : <PersonOutlineIcon sx={{ fontSize: 20, color: '#1976d2' }} />
                        }
                        <Box>
                            <Typography variant="body2" color="text.secondary">Sua participação</Typography>
                            <Typography variant="subtitle2" fontWeight={600}>
                                {isOwner ? 'Anfitriao' : 'Conviva'}
                            </Typography>
                        </Box>
                    </Stack>
                </Stack>
            </SectionCard>

            {/* Canal interno (Termos 1.11): a unica forma de o conviva falar com
                o anfitriao sobre o encontro dentro da Plataforma. E por isso que
                telefone, e-mail e Instagram sairam desta tela. */}
            {!isOwner && (
                <SectionCard>
                    <SectionTitle>Falar sobre o encontro</SectionTitle>
                    <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6, mb: 1.5 }}>
                        Dúvidas, ajustes e problemas técnicos deste encontro são tratados pela conversa da
                        Plataforma.
                    </Typography>
                    <BotaoConversar
                        orderId={id}
                        label={`Conversar com ${hostUser?.name?.split(' ')[0] || 'o anfitrião'}`}
                    />
                </SectionCard>
            )}

            {/* Sobre o anfitriao */}
            {hostUser?.description && (
                <SectionCard>
                    <SectionTitle>Sobre {hostUser.name?.split(' ')[0]}</SectionTitle>
                    <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>
                        {hostUser.description}
                    </Typography>
                </SectionCard>
            )}

            {/* Proposta do encontro */}
            <SectionCard>
                <SectionTitle>Proposta do encontro</SectionTitle>
                <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>
                    {meeting.description}
                </Typography>
            </SectionCard>

            {/* Encontro ja passou */}
            {isPast(addMinutes(parseISO(datetime), 30)) && (
                <Alert icon={<EventBusyIcon />} severity="info" sx={{ width: '100%', borderRadius: '12px' }}>
                    Este encontro já aconteceu.
                </Alert>
            )}

            {/* Alertas do sorteio: sao dirigidos ao conviva. Para o anfitriao,
                dizer que ele "nao foi contemplado" no proprio encontro nao faz
                sentido nenhum. */}
            {meeting.raffle && !isOwner && (() => {
                const hasOrders = meeting_scheduled?._count?.Order > 0;
                const raffleCompleted = meeting_scheduled?.Order?.length > 0;

                if (!hasOrders) {
                    return (
                        <Alert severity="info" sx={{ width: '100%', borderRadius: '12px' }}>
                            Aguardando participantes para a seleção.
                        </Alert>
                    );
                }
                if (!raffleCompleted) {
                    const prazo = meeting_scheduled?.raffle_at
                        ? new Date(meeting_scheduled.raffle_at).toLocaleString('pt-BR', {
                            dateStyle: 'short',
                            timeStyle: 'short',
                        })
                        : null;

                    return (
                        <Alert severity="info" sx={{ width: '100%', borderRadius: '12px' }}>
                            Aguardando o anfitrião realizar a seleção.
                            <Typography variant="body2" sx={{ mt: 1 }}>
                                {prazo
                                    ? `A seleção acontece até ${prazo}. Se não for feita pelo anfitrião até lá, o sistema faz a seleção automaticamente.`
                                    : 'Caso o anfitrião não cadastre uma data e horário limite para a seleção, ela ocorrerá 10 minutos antes do encontro.'}
                            </Typography>
                        </Alert>
                    );
                }
                if (chosen) {
                    return (
                        <Alert severity="success" sx={{ width: '100%', borderRadius: '12px' }}>
                            Parabéns! Você foi selecionado para participar deste encontro!
                        </Alert>
                    );
                }
                // Nao foi selecionado
                return (
                    <Alert severity="warning" sx={{ width: '100%', borderRadius: '12px' }}>
                        A seleção foi realizada e infelizmente você não foi contemplado.
                        {meeting.visible_public && meeting_scheduled?.id && (
                            <Typography variant="body2" sx={{ mt: 1 }}>
                                Você ainda pode assistir ao encontro como espectador.{' '}
                                <a
                                    href={`/lista/ao-vivo/${meeting_scheduled.id}`}
                                    style={{ color: '#006a61', fontWeight: 600 }}
                                >
                                    Assistir como espectador
                                </a>
                            </Typography>
                        )}
                    </Alert>
                );
            })()}

            {/* Pagamento pendente */}
            {data.status === 'PENDING' && !isOwner && <PaymentInfo data={data} mutate={mutate} />}

            {/* Botao de entrar / countdown */}
            {data.status === 'CONCLUDED' && (
                <CountdownButton
                    datetime={datetime}
                    chosen={chosen}
                    raffle={meeting.raffle}
                    visible={meeting.visible_public}
                    id={params.id}
                    scheduleId={meeting_scheduled?.id}
                    firstJoinedAt={data.first_joined_at}
                    finished={Boolean(data.finished)}
                    isOwner={isOwner}
                />
            )}

            {/* Anfitriao mudou o horario: o conviva pode sair sem prejuizo */}
            {!isOwner && Boolean(data.rescheduled_at) && ['CONCLUDED', 'PENDING'].includes(data.status) && (
                <HorarioAlterado
                    orderId={data.id}
                    anterior={data.previous_datetime}
                    atual={datetime}
                    gratuito={Boolean(meeting.free)}
                    onCancelado={mutate}
                />
            )}

            {/* Cancelamento pelo proprio conviva. Fica de fora quando o anfitriao
                trocou o horario: nesse caso quem oferece a saida e o
                HorarioAlterado acima, com reembolso mesmo em cima da hora. */}
            {!isOwner
                && !data.rescheduled_at
                && ['CONCLUDED', 'PENDING'].includes(data.status)
                && !isPast(parseISO(datetime)) && (
                <CancelarVaga
                    orderId={data.id}
                    datetime={datetime}
                    criadoEm={data.createdAt}
                    gratuito={Boolean(meeting.free)}
                    valor={data.amount ?? meeting.pricing}
                    status={data.status}
                    onCancelado={mutate}
                />
            )}

            {/* Vaga cancelada sem dinheiro envolvido (gratuito, ou pago em cima
                da hora, que nao gera devolucao) */}
            {data.status === 'CANCELED' && (
                <Alert severity="info" sx={{ width: '100%', borderRadius: '12px' }}>
                    Sua vaga neste encontro foi cancelada.
                </Alert>
            )}

            {/* Estorno */}
            {data.status === 'REFUNDED' && (
                <Alert severity="warning" sx={{ width: '100%', borderRadius: '12px' }}>
                    Pagamento estornado. Sua vaga foi cancelada.
                </Alert>
            )}
        </Box>
    );
};
